import { saveAs } from 'file-saver';

import { OsLotReviewScopeParams } from '../os-lot-review-scope';
import {
  OsLotReview,
  OsLotReviewAttestAllResponse,
  OsLotReviewAttestBulkResponse,
  OsLotReviewDetail,
  OsLotReviewsGetAllResponse,
  OsLotReviewsSummary,
  OsLotReviewSubmitPayload,
} from '../types/os-lot-reviews';

import {
  Filters,
  formatFilters,
  handleRequest,
  MultisectActionDto,
  privateClient,
} from '@data-access';

const OS_LOT_REVIEWS_EXPORT_TIMEOUT_MS = 60_000;

function parseExportFilename(contentDisposition: string | null): string {
  const match = contentDisposition
    ?.split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith('filename='));
  if (!match) {
    return 'revue-os-lots.xlsx';
  }
  return match.replace('filename=', '').replace(/"/g, '');
}

function appendSubmitBody(
  body: FormData,
  payload: OsLotReviewSubmitPayload,
) {
  body.append('status', payload.status);
  if (payload.comment) {
    body.append('comment', payload.comment);
  }
  if (payload.proposed_volume_reel != null) {
    body.append('proposed_volume_reel', String(payload.proposed_volume_reel));
  }
  if (payload.proposed_rdt_reel != null) {
    body.append('proposed_rdt_reel', String(payload.proposed_rdt_reel));
  }
  if (payload.proposed_declared_telepac_id) {
    body.append(
      'proposed_declared_telepac_id',
      payload.proposed_declared_telepac_id,
    );
  }
  if (payload.proposed_telepac) {
    body.append('proposed_telepac', payload.proposed_telepac);
  }
  payload.proposed_telepacs?.forEach((file) => {
    body.append('proposed_telepacs', file);
  });
}

export const osLotReviewsApi = {
  getAll: async (filters: Filters) => {
    return handleRequest<OsLotReviewsGetAllResponse>(
      privateClient
        .get('os-lot-reviews/', {
          searchParams: {
            ...formatFilters(filters),
          },
        })
        .json(),
    );
  },

  getSummary: async (columnFilters: Filters['filters']) => {
    return handleRequest<OsLotReviewsSummary>(
      privateClient
        .get('os-lot-reviews/summary/', {
          searchParams: {
            ...formatFilters({
              page: 1,
              sorting: [],
              filters: columnFilters,
            }),
          },
        })
        .json(),
    );
  },

  exportExcel: async (filters: Filters) => {
    const response = await handleRequest(
      privateClient
        .extend({ timeout: OS_LOT_REVIEWS_EXPORT_TIMEOUT_MS })
        .get('os-lot-reviews/export/', {
          searchParams: {
            ...formatFilters({
              page: 1,
              sorting: filters.sorting,
              filters: filters.filters,
            }),
          },
        }),
    );
    const filename = parseExportFilename(
      response.headers.get('content-disposition'),
    );
    const blob = await response.blob();
    saveAs(blob, filename);
  },

  getDetail: async (lotId: number, scope: OsLotReviewScopeParams) => {
    return handleRequest<OsLotReviewDetail>(
      privateClient
        .get(`os-lot-reviews/${lotId}/`, {
          searchParams: scope,
        })
        .json(),
    );
  },

  submitLink: async (
    lotId: number,
    linkId: number,
    payload: OsLotReviewSubmitPayload,
    scope: OsLotReviewScopeParams,
  ) => {
    const body = new FormData();
    appendSubmitBody(body, payload);
    return handleRequest<OsLotReview>(
      privateClient
        .post(`os-lot-reviews/${lotId}/links/${linkId}/submit/`, {
          body,
          searchParams: scope,
        })
        .json(),
    );
  },

  attestAll: async (
    lotId: number,
    scope: OsLotReviewScopeParams,
    comment?: string,
  ) => {
    const body = new FormData();
    if (comment) {
      body.append('comment', comment);
    }
    return handleRequest<OsLotReviewAttestAllResponse>(
      privateClient
        .post(`os-lot-reviews/${lotId}/attest-all/`, {
          body,
          searchParams: scope,
        })
        .json(),
    );
  },

  attestBulk: async (
    payload: MultisectActionDto,
    scope: OsLotReviewScopeParams,
  ) => {
    return handleRequest<OsLotReviewAttestBulkResponse>(
      privateClient
        .post('os-lot-reviews/attest-bulk/', {
          json: payload,
          searchParams: scope,
        })
        .json(),
    );
  },
};
